import { formatMoney } from '@/lib/format';
import type { ProductProfitRow, ProfitSortKey } from '@/lib/profits';

interface ProfitRankingTableProps {
  rows: ProductProfitRow[];
  sortKey: ProfitSortKey;
  onSort: (key: ProfitSortKey) => void;
  currencySymbol: string;
}

interface Column {
  key: ProfitSortKey | null;
  label: string;
  align: 'left' | 'right';
}

const COLUMNS: Column[] = [
  { key: null, label: 'Producto', align: 'left' },
  { key: 'quantity', label: 'Vendidos', align: 'right' },
  { key: 'revenue', label: 'Ingresos', align: 'right' },
  { key: null, label: 'Costo', align: 'right' },
  { key: 'profit', label: 'Ganancia', align: 'right' },
  { key: 'margin', label: 'Margen', align: 'right' },
];

export default function ProfitRankingTable({
  rows,
  sortKey,
  onSort,
  currencySymbol,
}: ProfitRankingTableProps) {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-background-300 p-10 text-center">
        <i className="ri-line-chart-line text-4xl text-foreground-400" />
        <p className="mt-3 text-sm text-foreground-600">
          No hay ventas en el período seleccionado para calcular ganancias.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-heading text-lg font-semibold text-foreground-950">Ranking de productos</h2>
        <span className="text-xs text-foreground-500">{rows.length} productos</span>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="text-foreground-600 text-left border-b border-background-200">
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.label}
                  className={`py-2 font-medium ${col.align === 'right' ? 'text-right' : ''}`}
                >
                  {col.key ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key as ProfitSortKey)}
                      className={`inline-flex items-center gap-1 whitespace-nowrap transition cursor-pointer ${
                        sortKey === col.key ? 'text-primary-700 font-semibold' : 'hover:text-foreground-900'
                      }`}
                    >
                      {col.label}
                      <i
                        className={`text-xs ${
                          sortKey === col.key ? 'ri-arrow-down-line' : 'ri-arrow-up-down-line'
                        }`}
                      />
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-background-100">
            {rows.map((r) => (
              <tr key={r.productId ?? r.name} className="hover:bg-background-100/60 transition">
                <td className="py-2.5 pr-4">
                  <div className="text-foreground-900 font-medium">{r.name}</div>
                  <div className="text-xs text-foreground-500">
                    {r.sku ? `SKU ${r.sku}` : 'Sin SKU'}
                    {r.categoryName ? ` · ${r.categoryName}` : ''}
                  </div>
                </td>
                <td className="py-2.5 text-right text-foreground-700">{r.quantity}</td>
                <td className="py-2.5 text-right text-foreground-700">
                  {formatMoney(r.revenue, currencySymbol)}
                </td>
                <td className="py-2.5 text-right text-foreground-700">
                  {formatMoney(r.cost, currencySymbol)}
                </td>
                <td
                  className={`py-2.5 text-right font-semibold ${
                    r.profit < 0 ? 'text-accent-700' : 'text-foreground-950'
                  }`}
                >
                  {formatMoney(r.profit, currencySymbol)}
                </td>
                <td className="py-2.5 text-right">
                  <span
                    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                      r.margin < 0
                        ? 'bg-accent-100 text-accent-900'
                        : 'bg-secondary-100 text-secondary-900'
                    }`}
                  >
                    {r.margin.toFixed(1)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}